// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { Message } from '../api/models/Message';
import { MessageContent } from '../api/models/MessageContent';
import { Conversation } from '../api/models/Conversation';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import ImageModal, { ImgModalState } from './image-modal';
import { ArrowTopRightOnSquareIcon, ArrowDownTrayIcon, BookmarkIcon, ChartBarIcon } from "@heroicons/react/24/solid"; // Importing icons
import { CodeInterpreterResults } from './code-modal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createArtifact } from '../api/thunks/artifacts';
import { useAppSelector } from '../store/hooks';
import { useDispatch } from 'react-redux';
import { createCheckoutSession } from '../api/thunks/users';
import { parseStructuredMarkdown } from '../utils/structured-markdown';
import { DirectiveRenderer } from './directive-renderer';
import { WorkflowPreviewComponent } from './workflow-preview-component';
import InputWithSuggestions from './input-with-suggestions';
import { Brain, ChevronDown, Database, FileSearch, FileText, Share2Icon, Telescope, Wrench } from 'lucide-react';
import { setNotification } from '../store/notifications-reducer';
import TypingDots from './typing-dots';
import { WebSearchAnnotation, WebSearchResult } from '../api/models/WebSearchAnnotation';
import { VectorStoreSearchResult } from '../api/models/FileSearchAnnotation';
import { Effort, Model } from '../api/models/CreateMessageInput';
import { FinancialChartModal } from './financial-chart-modal';
import {
    formatTableCellValue,
    getTableCellValueType,
    UITableValueType,
} from '../utils/ui-table-formatting';

// CSV export utility function
const exportTableToCSV = (tableElement: HTMLTableElement, filename: string = 'table-data') => {
    const rows = Array.from(tableElement.querySelectorAll('tr'));
    const csvContent = rows.map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        return cells.map(cell => {
            // Clean the cell content and escape quotes
            const content = cell.textContent?.trim() || '';
            // Escape quotes by doubling them and wrap in quotes if contains comma, quote, or newline
            if (content.includes(',') || content.includes('"') || content.includes('\n')) {
                return `"${content.replace(/"/g, '""')}"`;
            }
            return content;
        }).join(',');
    }).join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// UI Table types and formatting utilities
interface UITableColumn {
    key: string;
    header: string;
    // Freeform columns are rendered verbatim rather than being coerced to a numeric unit.
    type: UITableValueType;
}

interface UITableData {
    title: string;
    columns: UITableColumn[];
    rows: Record<string, any>[];
}

const parseUITableData = (args: unknown): UITableData | undefined => {
    try {
        const parsed = typeof args === 'string' ? JSON.parse(args) : args;
        if (!parsed || typeof parsed !== 'object') return undefined;

        const table = parsed as UITableData;
        if (!table.title || !Array.isArray(table.columns) || !Array.isArray(table.rows)) return undefined;
        return table;
    } catch {
        return undefined;
    }
};

const getPQTableID = (output: unknown): string | undefined => {
    if (typeof output === 'number' && Number.isInteger(output) && output > 0) return String(output);
    if (typeof output === 'string') {
        const id = output.trim();
        return /^\d+$/.test(id) ? id : undefined;
    }
    if (output && typeof output === 'object' && 'id' in output) {
        return getPQTableID((output as { id?: unknown }).id);
    }
    return undefined;
};

const getPQTablesByID = (runDetails: unknown): Map<string, UITableData> => {
    const tables = new Map<string, UITableData>();
    if (!Array.isArray(runDetails)) return tables;

    for (const call of runDetails) {
        if (call?.type !== 'function' || call?.name !== 'render_ui_table') continue;
        const id = getPQTableID(call.output);
        const table = parseUITableData(call.args);
        if (id && table) tables.set(id, table);
    }
    return tables;
};

// Extract chart data from UI table data
const extractChartDataFromUITable = (tableData: UITableData) => {
    const { columns, rows } = tableData;
    if (rows.length === 0 || columns.length === 0) return { data: [], datasets: [] };

    // Find period/label column (first string column or first column)
    const periodCol = columns.find(c => c.type === 'string') || columns[0];
    const dataColumns = columns.filter(c => c.key !== periodCol.key && (c.type === 'number' || c.type === 'currency' || c.type === 'percent'));

    const data = rows.map(row => {
        const dataPoint: any = { period: row[periodCol.key] || '' };
        dataColumns.forEach(col => {
            const val = row[col.key];
            dataPoint[col.header] = typeof val === 'number' ? val : parseFloat(val) || 0;
        });
        return dataPoint;
    });

    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];
    const datasets = dataColumns.map((col, index) => ({
        label: col.header,
        units: col.type === 'currency' ? 'USD' : col.type === 'percent' ? '%' : '',
        color: colors[index % colors.length]
    }));

    return { data, datasets };
};

// Chart data extraction utility function
const extractChartDataFromTable = (tableElement: HTMLTableElement) => {
    const rows = Array.from(tableElement.querySelectorAll('tr'));
    if (rows.length < 2) return { data: [], datasets: [] };

    const headerRow = rows[0];
    const headers = Array.from(headerRow.querySelectorAll('th, td')).map(cell => cell.textContent?.trim() || '');

    // Assume first column is period, rest are data columns
    const periodColumn = headers[0];
    const dataColumns = headers.slice(1);

    const data = rows.slice(1).map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        const period = cells[0]?.textContent?.trim() || '';
        const dataPoint: any = { period };

        cells.slice(1).forEach((cell, index) => {
            const columnName = dataColumns[index];
            if (columnName) {
                const cellText = cell.textContent?.trim() || '';
                // Try to parse as number, removing common currency symbols and formatting
                const cleanedText = cellText.replace(/[$,B,M,K,%]/g, '').replace(/[()]/g, '-');
                const numericValue = parseFloat(cleanedText);

                if (!isNaN(numericValue)) {
                    // Handle B, M, K multipliers
                    let multiplier = 1;
                    if (cellText.includes('B')) multiplier = 1000000000;
                    else if (cellText.includes('M')) multiplier = 1000000;
                    else if (cellText.includes('K')) multiplier = 1000;

                    dataPoint[columnName] = numericValue * multiplier;
                } else {
                    // Keep text values as-is instead of converting to 0
                    dataPoint[columnName] = cellText || '';
                }
            }
        });

        return dataPoint;
    });

    // Generate colors for datasets
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];
    const datasets = dataColumns.map((column, index) => ({
        label: column,
        units: 'USD', // Default to USD, could be enhanced to detect units
        color: colors[index % colors.length]
    }));

    return { data, datasets };
};

// Simple React component
export const LlmConvoComponent = ({ createShareLink, convo, sendMessage, message, setMessage, getCitationTextDetails, highlighter, setImgModalState, openCodeModal, createArtifact, questionSuggestions, messageInProgress, onWorkflowCreated }:
    {
        createShareLink: (conversationId: string) => void;
        convo: Conversation | undefined
        sendMessage: (model: Model, effortLevel?: Effort) => Promise<boolean>;
        message: string;
        setMessage: (message: string) => void;
        getCitationTextDetails: (conversationId: string, messageId: string, fileId: string) => Promise<string>;
        setImgModalState: (state: ImgModalState) => void;
        openCodeModal: (val: string) => void;
        createArtifact: (type: 'code' | 'image', data: string) => Promise<void>;
        questionSuggestions: string[];
        messageInProgress?: boolean;
        onWorkflowCreated?: () => void;
    }
) => {
    const messageDiv = document.getElementById('message-div');
    const [tooltip, setTooltip] = useState({ visible: false, title: '', position: { x: 0, y: 0 } });
    const [isShowingFullCitation, setIsShowingFullCitation] = useState(false);
    const [messageInFlight, setMessageInFlight] = useState(false);
    const [expandedDetails, setExpandedDetails] = useState<{ [key: string]: boolean }>({});
    const [chartModalOpen, setChartModalOpen] = useState(false);
    const [chartData, setChartData] = useState<{ data: any[], datasets: any[] }>({ data: [], datasets: [] });
    const user = useAppSelector(state => state.user);
    const dispatch = useDispatch();
    const [selectedModel, setSelectedModel] = useState<Model>(() => {
        const query = new URLSearchParams(window.location.search);
        return (query.get('model') as Model) || "gpt-5.6-sol";
    });
    const [effortLevel, setEffortLevel] = useState<Effort | undefined>("high");

    // Persist model selection in URL
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        if (selectedModel !== query.get('model')) {
            query.set('model', selectedModel);
            window.history.replaceState(null, '', `${window.location.pathname}?${query.toString()}`);
        }
    }, [selectedModel]);

    const memoizedMessageData = useMemo(() => {
        const cache = new Map();
        if (convo && convo.messages) {
            for (const msg of convo.messages) {
                if (msg.id && msg.role !== Message.RoleEnum.User && msg.runDetails) {
                    const fileSearchToolCalls = msg.runDetails.filter(call => call.type === "function" && call.name == "file_search") || [];
                    let vectorStoreSearchResults: VectorStoreSearchResult[] = [];
                    for (const call of fileSearchToolCalls) {
                        try {
                            if (typeof call.output === 'string') {
                                const parsedOutput = JSON.parse(call.output);
                                if (parsedOutput && Array.isArray(parsedOutput.results)) {
                                    vectorStoreSearchResults.push(...parsedOutput.results);
                                }
                            }
                        } catch (e) {
                            // console.log("failed to json parse fileSearchToolCalls output", call, e); // Log includes error object e
                        }
                    }
                    cache.set(msg.id, { fileSearchToolCalls, vectorStoreSearchResults });
                } else if (msg.id) {
                    cache.set(msg.id, { fileSearchToolCalls: [], vectorStoreSearchResults: [] });
                }
            }
        }
        return cache;
    }, [convo?.messages]);


    const showTooltip = (title: string, event: React.MouseEvent) => {
        const tooltipHeight = 100; // Height of the tooltip in px
        const yOffset = 10; // Offset from the clicked citation

        setTooltip({
            visible: true,
            title: title,
            position: { x: event.clientX, y: event.clientY - tooltipHeight - yOffset } // Adjust tooltip position to appear above
        });
    };

    const hideTooltip = () => {
        setTooltip({ visible: false, title: '', position: { x: 0, y: 0 } });
    };


    // useEffect(() => {
    //     if (messageDiv) {
    //         messageDiv.scrollTop = messageDiv.scrollHeight;
    //     }
    // }, [messageDiv, convo]);

    const renderUITable = (tableData: UITableData, tableKey: string) => (
        <div key={`pq-table-${tableKey}`} className="mb-4">
            <div className="overflow-x-auto my-4 relative">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">{tableData.title}</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                const { data, datasets } = extractChartDataFromUITable(tableData);
                                setChartData({ data, datasets });
                                setChartModalOpen(true);
                            }}
                            className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                            title="Create Chart"
                        >
                            <ChartBarIcon className="w-3 h-3" />
                            Chart
                        </button>
                        <button
                            onClick={() => {
                                const headers = tableData.columns.map(c => c.header);
                                const csvContent = [
                                    headers.join(','),
                                    ...tableData.rows.map(row =>
                                        tableData.columns.map(col => {
                                            const val = formatTableCellValue(row[col.key], getTableCellValueType(row, col));
                                            return val.includes(',') || val.includes('"') || val.includes('\n')
                                                ? `"${val.replace(/"/g, '""')}"`
                                                : val;
                                        }).join(','),
                                    ),
                                ].join('\n');
                                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                const link = document.createElement('a');
                                const url = URL.createObjectURL(blob);
                                link.setAttribute('href', url);
                                link.setAttribute('download', `${tableData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`);
                                link.style.visibility = 'hidden';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                            title="Export to CSV"
                        >
                            <ArrowDownTrayIcon className="w-3 h-3" />
                            CSV
                        </button>
                    </div>
                </div>
                <table className="min-w-full border-collapse border border-gray-300">
                    <thead className="bg-gray-50">
                        <tr className="border-b border-gray-200">
                            {tableData.columns.map((col, colIdx) => (
                                <th
                                    key={`${tableKey}-th-${colIdx}`}
                                    className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap"
                                >
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.rows.map((row, rowIdx) => (
                            <tr key={`${tableKey}-tr-${rowIdx}`} className="border-b border-gray-200 hover:bg-gray-50">
                                {tableData.columns.map((col, colIdx) => (
                                    <td
                                        key={`${tableKey}-td-${rowIdx}-${colIdx}`}
                                        className={`border border-gray-300 px-4 py-2 ${col.type === 'number' || col.type === 'currency' || col.type === 'percent' ? 'text-right' : ''}`}
                                    >
                                        {formatTableCellValue(row[col.key], getTableCellValueType(row, col))}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    function addCitationLinksToMessage(
        content: MessageContent,
        rootMessage?: Message,
        msgIdx: number,
    ) {
        const messageSpecificData = rootMessage?.id ? memoizedMessageData.get(rootMessage.id) : undefined;
        const currentVectorStoreSearchResults = messageSpecificData?.vectorStoreSearchResults || [];

        if (!content?.text) return null;

        const { cleanedText, directives } = parseStructuredMarkdown(content.text);
        const tableByID = getPQTablesByID(rootMessage?.runDetails);
        const latexRegex = /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\])/g;
        const latexDelimiters = [
            { left: "$$", right: "$$", display: true },
            { left: "\\[", right: "\\]", display: true },
        ];
        const parts: React.ReactNode[] = [];
        let partIndex = 0;

        const renderTextWithLatex = (text: string) => {
            const textParts: React.ReactNode[] = [];
            let lastIndex = 0;
            for (const latexMatch of text.matchAll(latexRegex)) {
                const startIndex = latexMatch.index!;
                const endIndex = startIndex + latexMatch[0].length;
                if (lastIndex < startIndex) {
                    textParts.push(processTextSegment(text.slice(lastIndex, startIndex), currentVectorStoreSearchResults, partIndex++, rootMessage, msgIdx));
                }
                textParts.push(
                    <div key={`latex-container-${partIndex++}`} style={{ overflowX: 'scroll' }}>
                        <Latex delimiters={latexDelimiters}>{latexMatch[0]}</Latex>
                    </div>,
                );
                lastIndex = endIndex;
            }
            if (lastIndex < text.length) {
                textParts.push(processTextSegment(text.slice(lastIndex), currentVectorStoreSearchResults, partIndex++, rootMessage, msgIdx));
            }
            if (textParts.length === 0 && text) {
                textParts.push(processTextSegment(text, currentVectorStoreSearchResults, partIndex++, rootMessage, msgIdx));
            }
            return textParts;
        };

        // ReactMarkdown intentionally strips unknown HTML. Split pqTable XML
        // ourselves so a table appears exactly where the model placed its tag.
        const tableTagRegex = /<pqTable\s+id=(['"])(\d+)\1\s*\/>/gi;
        let lastTableIndex = 0;
        let tableOccurrence = 0;
        for (const match of cleanedText.matchAll(tableTagRegex)) {
            const startIndex = match.index!;
            if (lastTableIndex < startIndex) {
                parts.push(...renderTextWithLatex(cleanedText.slice(lastTableIndex, startIndex)));
            }
            const tableID = match[2];
            const tableData = tableByID.get(tableID);
            if (tableData) {
                parts.push(renderUITable(tableData, `${rootMessage?.id || 'message'}-${tableID}-${tableOccurrence}`));
            }
            tableOccurrence++;
            lastTableIndex = startIndex + match[0].length;
        }
        if (lastTableIndex < cleanedText.length) {
            parts.push(...renderTextWithLatex(cleanedText.slice(lastTableIndex)));
        }
        if (parts.length === 0 && cleanedText) {
            parts.push(...renderTextWithLatex(cleanedText));
        }

        directives.forEach((directive, index) => {
            parts.push(<DirectiveRenderer key={`directive-${index}`} directive={directive} />);
        });

        return <>{parts}</>;
    }

    function processTextSegment(
        text: string,
        vectorStoreSearchResults: VectorStoreSearchResult[],
        partIndex: number,
        rootMessage?: Message,
        msgIdx: number = 0
    ): React.ReactNode {
        if (!text.trim()) return null;

        // Step 1: Process through ReactMarkdown first
        const citationRegex = /【([^†]+?)†?(.+?)】/g;
        const citationMatches = []; // Array.from(text.matchAll(citationRegex)); // temporarily disable citation formatting because it breaks tables and the transcript lookup isn't even that great right now

        if (citationMatches.length === 0) {
            // No citations, just render as markdown
            return (
                <ReactMarkdown
                    key={`markdown-${partIndex}`}
                    remarkPlugins={[remarkGfm]} components={{
                        p: ({ node, ...props }) => {
                            // Convert bullet point lines to proper list items
                            const content = props.children;
                            if (typeof content === 'string' && (content.includes('•') || content.includes('✓'))) {
                                const lines = content.split('\n').filter(line => line.trim());
                                const hasMultipleBullets = lines.filter(line => line.includes('•') || line.includes('✓')).length > 1;

                                if (hasMultipleBullets) {
                                    return (
                                        <ul className="list-disc ml-5 mb-2">
                                            {lines.map((line, index) => {
                                                if (line.includes('•') || line.includes('✓')) {
                                                    const cleanLine = line.replace(/^[\s]*[•✓][\s]*/, '').trim();
                                                    return <li key={index} className="mb-1">{cleanLine}</li>;
                                                } else if (line.trim()) {
                                                    return <p key={index} className="mb-2">{line}</p>;
                                                }
                                                return null;
                                            })}
                                        </ul>
                                    );
                                }
                            }
                            return <p className="mb-2" {...props} />;
                        },
                        ul: ({ node, ...props }) => (
                            <ul className="list-disc ml-5" {...props} />
                        ),
                        li: ({ node, ...props }) => (
                            <li className="mb-1" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                            <a className="text-blue-500 underline" {...props} />
                        ),
                        table: ({ node, ...props }) => {
                            const tableId = `table-${Math.random().toString(36).substr(2, 9)}`;
                            return (
                                <div className="overflow-x-auto my-4 relative">
                                    <div className="flex justify-end mb-2 gap-2">
                                        <button
                                            onClick={() => {
                                                const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                if (tableElement) {
                                                    const { data, datasets } = extractChartDataFromTable(tableElement);
                                                    setChartData({ data, datasets });
                                                    setChartModalOpen(true);
                                                }
                                            }}
                                            className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                            title="Create Chart"
                                        >
                                            <ChartBarIcon className="w-3 h-3" />
                                            Chart
                                        </button>
                                        <button
                                            onClick={() => {
                                                const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                if (tableElement) {
                                                    exportTableToCSV(tableElement, 'financial-data');
                                                }
                                            }}
                                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                            title="Export to CSV"
                                        >
                                            <ArrowDownTrayIcon className="w-3 h-3" />
                                            CSV
                                        </button>
                                    </div>
                                    <table
                                        className="min-w-full border-collapse border border-gray-300"
                                        data-table-id={tableId}
                                        {...props}
                                    />
                                </div>
                            );
                        },
                        thead: ({ node, ...props }) => (
                            <thead className="bg-gray-50" {...props} />
                        ),
                        tbody: ({ node, ...props }) => (
                            <tbody {...props} />
                        ),
                        tr: ({ node, ...props }) => (
                            <tr className="border-b border-gray-200" {...props} />
                        ),
                        th: ({ node, ...props }) => (
                            <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                            <td className="border border-gray-300 px-4 py-2" {...props} />
                        )
                    }}
                >
                    {convertToMarkdownTable(text)}
                </ReactMarkdown>
            );
        }

        // Step 2: Handle citations by splitting text and wrapping citations
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let citationIndex = 0;

        for (const match of citationMatches) {
            const [citationText, citationTextFileNameOrFileId] = match;
            const startIndex = match.index!;
            const endIndex = startIndex + citationText.length;

            // Add text before citation as markdown
            if (lastIndex < startIndex) {
                const beforeText = text.slice(lastIndex, startIndex);
                if (beforeText.trim()) {
                    parts.push(
                        <ReactMarkdown
                            key={`markdown-before-${partIndex}-${citationIndex}`}
                            remarkPlugins={[remarkGfm]}
                            components={{
                                p: ({ node, ...props }) => {
                                    // Convert bullet point lines to proper list items
                                    const content = props.children;
                                    if (typeof content === 'string' && (content.includes('•') || content.includes('✓'))) {
                                        const lines = content.split('\n').filter(line => line.trim());
                                        const hasMultipleBullets = lines.filter(line => line.includes('•') || line.includes('✓')).length > 1;

                                        if (hasMultipleBullets) {
                                            return (
                                                <ul className="list-disc ml-5 mb-2">
                                                    {lines.map((line, index) => {
                                                        if (line.includes('•') || line.includes('✓')) {
                                                            const cleanLine = line.replace(/^[\s]*[•✓][\s]*/, '').trim();
                                                            return <li key={index} className="mb-1">{cleanLine}</li>;
                                                        } else if (line.trim()) {
                                                            return <p key={index} className="mb-2">{line}</p>;
                                                        }
                                                        return null;
                                                    })}
                                                </ul>
                                            );
                                        }
                                    }
                                    return <p className="mb-2" {...props} />;
                                },
                                ul: ({ node, ...props }) => (
                                    <ul className="list-disc ml-5" {...props} />
                                ),
                                li: ({ node, ...props }) => (
                                    <li className="mb-1" {...props} />
                                ),
                                a: ({ node, ...props }) => (
                                    <a className="text-blue-500 underline" {...props} />
                                ),
                                table: ({ node, ...props }) => {
                                    const tableId = `table-${Math.random().toString(36).substr(2, 9)}`;
                                    return (
                                        <div className="overflow-x-auto my-4 relative">
                                            <div className="flex justify-end mb-2 gap-2">
                                                <button
                                                    onClick={() => {
                                                        const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                        if (tableElement) {
                                                            const { data, datasets } = extractChartDataFromTable(tableElement);
                                                            setChartData({ data, datasets });
                                                            setChartModalOpen(true);
                                                        }
                                                    }}
                                                    className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                    title="Create Chart"
                                                >
                                                    <ChartBarIcon className="w-3 h-3" />
                                                    Chart
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                        if (tableElement) {
                                                            exportTableToCSV(tableElement, 'financial-data');
                                                        }
                                                    }}
                                                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                    title="Export to CSV"
                                                >
                                                    <ArrowDownTrayIcon className="w-3 h-3" />
                                                    CSV
                                                </button>
                                            </div>
                                            <table
                                                className="min-w-full border-collapse border border-gray-300"
                                                data-table-id={tableId}
                                                {...props}
                                            />
                                        </div>
                                    );
                                },
                                thead: ({ node, ...props }) => (
                                    <thead className="bg-gray-50" {...props} />
                                ),
                                tbody: ({ node, ...props }) => (
                                    <tbody {...props} />
                                ),
                                tr: ({ node, ...props }) => (
                                    <tr className="border-b border-gray-200" {...props} />
                                ),
                                th: ({ node, ...props }) => (
                                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                                ),
                                td: ({ node, ...props }) => (
                                    <td className="border border-gray-300 px-4 py-2" {...props} />
                                )
                            }}
                        >
                            {convertToMarkdownTable(beforeText)}
                        </ReactMarkdown>
                    );
                }
            }

            // Add citation link
            let fullCitation = '';
            for (const c of vectorStoreSearchResults) {
                if (c.file_id === citationTextFileNameOrFileId || c.filename === citationTextFileNameOrFileId) {
                    fullCitation += c.content.map((contentItem) => contentItem.text).join('\n');
                }
            }

            parts.push(
                <a
                    key={`citation-${partIndex}-${citationIndex}`}
                    className="text-blue-500 hover:underline cursor-pointer break-words"
                    style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto'
                    }}
                    onClick={async (e) => {
                        setIsShowingFullCitation(true);
                        if (window.location.href.includes('try-it')) {
                            fullCitation = await getCitationTextDetails(convo?.id || '', rootMessage?.id || '', citationTextFileNameOrFileId);
                        }
                        showTooltip(
                            `${citationText.split(".")[0]} ${fullCitation}`,
                            e
                        );
                    }}
                >
                    {citationText}
                </a>
            );

            lastIndex = endIndex;
            citationIndex++;
        }

        // Add remaining text after last citation as markdown
        if (lastIndex < text.length) {
            const afterText = text.slice(lastIndex);
            if (afterText.trim()) {
                parts.push(
                    <ReactMarkdown
                        key={`markdown-after-${partIndex}`}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ node, ...props }) => {
                                // Convert bullet point lines to proper list items
                                const content = props.children;
                                if (typeof content === 'string' && (content.includes('•') || content.includes('✓'))) {
                                    const lines = content.split('\n').filter(line => line.trim());
                                    const hasMultipleBullets = lines.filter(line => line.includes('•') || line.includes('✓')).length > 1;

                                    if (hasMultipleBullets) {
                                        return (
                                            <ul className="list-disc ml-5 mb-2">
                                                {lines.map((line, index) => {
                                                    if (line.includes('•') || line.includes('✓')) {
                                                        const cleanLine = line.replace(/^[\s]*[•✓][\s]*/, '').trim();
                                                        return <li key={index} className="mb-1">{cleanLine}</li>;
                                                    } else if (line.trim()) {
                                                        return <p key={index} className="mb-2">{line}</p>;
                                                    }
                                                    return null;
                                                })}
                                            </ul>
                                        );
                                    }
                                }
                                return <p className="mb-2" {...props} />;
                            },
                            ul: ({ node, ...props }) => (
                                <ul className="list-disc ml-5" {...props} />
                            ),
                            li: ({ node, ...props }) => (
                                <li className="mb-1" {...props} />
                            ),
                            a: ({ node, ...props }) => (
                                <a className="text-blue-500 underline" {...props} />
                            ),
                            table: ({ node, ...props }) => {
                                const tableId = `table-${Math.random().toString(36).substr(2, 9)}`;
                                return (
                                    <div className="overflow-x-auto my-4 relative">
                                        <div className="flex justify-end mb-2 gap-2">
                                            <button
                                                onClick={() => {
                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                    if (tableElement) {
                                                        const { data, datasets } = extractChartDataFromTable(tableElement);
                                                        setChartData({ data, datasets });
                                                        setChartModalOpen(true);
                                                    }
                                                }}
                                                className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                title="Create Chart"
                                            >
                                                <ChartBarIcon className="w-3 h-3" />
                                                Chart
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                    if (tableElement) {
                                                        exportTableToCSV(tableElement, 'financial-data');
                                                    }
                                                }}
                                                className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                title="Export to CSV"
                                            >
                                                <ArrowDownTrayIcon className="w-3 h-3" />
                                                CSV
                                            </button>
                                        </div>
                                        <table
                                            className="min-w-full border-collapse border border-gray-300"
                                            data-table-id={tableId}
                                            {...props}
                                        />
                                    </div>
                                );
                            },
                            thead: ({ node, ...props }) => (
                                <thead className="bg-gray-50" {...props} />
                            ),
                            tbody: ({ node, ...props }) => (
                                <tbody {...props} />
                            ),
                            tr: ({ node, ...props }) => (
                                <tr className="border-b border-gray-200" {...props} />
                            ),
                            th: ({ node, ...props }) => (
                                <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                            ),
                            td: ({ node, ...props }) => (
                                <td className="border border-gray-300 px-4 py-2" {...props} />
                            )
                        }}
                    >
                        {convertToMarkdownTable(afterText)}
                    </ReactMarkdown>
                );
            }
        }

        return <>{parts}</>;
    }

    const sendMessageLocal = async (messageOverride?: string) => {
        if (messageInProgress || messageInFlight) return;
        setMessageInFlight(true);
        const res = await sendMessage(selectedModel, effortLevel, messageOverride);
        if (res) {
            setMessage('');
        }
        setMessageInFlight(false);
    }

    const handleToggleDetails = (id: string) => {
        setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatFunctionName = (str) => {
        if (!str) {
            return "";
        }
        // Replace underscores with spaces, then add space before capital letters and convert to lowercase
        return str
            .replace(/_/g, ' ')                    // Replace underscores with spaces
            .replace(/([a-z])([A-Z])/g, '$1 $2')    // Add space before capital letters
            .toLowerCase();                        // Convert the entire string to lowercase
    };

    const getToolCallIconType = (name: string) => {
        const normalizedName = (name || '').toLowerCase();
        if (normalizedName.includes('search')) return 'search';
        if (normalizedName.includes('file') || normalizedName.includes('load')) return 'file';
        if (normalizedName.includes('memory')) return 'memory';
        if (normalizedName.includes('earnings')) return 'earnings';
        if (normalizedName.includes('statement') || normalizedName.includes('data')) return 'data';
        return 'action';
    };

    const renderToolCallIcon = (name: string, size = 14) => {
        switch (getToolCallIconType(name)) {
        case 'search': return <Telescope size={size} />;
        case 'file': return <FileText size={size} />;
        case 'memory': return <BookmarkIcon style={{ width: size, height: size }} />;
        case 'earnings': return <ChartBarIcon style={{ width: size, height: size }} />;
        case 'data': return <Database size={size} />;
        default: return <Wrench size={size} />;
        }
    };

    const formatFuncCallOutput = (call: any) => {
        if (call.name === 'web_search') {
            try {
                const res = JSON.parse(call.output) as WebSearchResult;
                return <ul className='list-disc ml-5'>
                    {res.annotations.map((annotation, idx) => {
                        const annotationId = `web-search-annotation-${call.id}-${idx}`;
                        return (
                            <li key={annotationId} className="mb-2 li-disc">
                                <a
                                    className="tool-title text-blue-400 font-size-10 cursor-pointer"
                                    href={annotation.url}
                                    target="_blank"
                                >
                                    {annotation.title}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            } catch (e) {
                return call.output;
            }
        } else if (call.name === 'file_search') {
            try {
                const toolCallResults = JSON.parse(call.output);
                const fileSearchResults = toolCallResults.results as VectorStoreSearchResult[]; // Ensure type consistency
                return fileSearchResults.map((result, idx) => {
                    const fileId = `file-search-result-${call.id}-${idx}`;
                    return (
                        <div key={fileId} className="mb-2">
                            <a
                                className="tool-title text-blue-400 cursor-pointer"
                                onClick={() => handleToggleDetails(fileId)}
                            >
                                {result.file_name || result.file_id}
                            </a>
                            {expandedDetails[fileId] && (
                                <div className="ml-4 mt-1 p-2 border-l-4 border-blue-300 bg-blue-50">
                                    {result.content.map((contentItem, contentIdx) => (
                                        <p key={contentIdx} style={{ overflowWrap: "break-word" }}>
                                            {contentItem.text}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                });
            } catch (e) {
                console.log('Error parsing file search results:', e);
                return call.output
            }
        }

        return call.output
    }

    const renderRunDetails = (msg: Message) => {
        const runDetails = Array.isArray(msg.runDetails) ? msg.runDetails : [];
        // Tables belong to explicit <pqTable> positions in the message, not in
        // the trailing tool-details section.
        const allFunctionCalls = runDetails.filter(call => call.type === "function" && call.name !== "file_search" && call.name !== "render_ui_table");
        // Prefer the latest attempt when the model corrects an invalid proposal
        // in the same response.
        const buildWorkflowCall = [...allFunctionCalls].reverse().find(call => call.name === "build_workflow");
        // A workflow proposal is useful as soon as the function call has been
        // emitted. The backend sends the arguments before the final result, so
        // fall back to them instead of waiting for a page refresh/re-hydration.
        const workflowProposalJson = buildWorkflowCall
            ? (typeof buildWorkflowCall.output === 'string' && buildWorkflowCall.output.trim()
                ? buildWorkflowCall.output
                : typeof buildWorkflowCall.args === 'string'
                    ? buildWorkflowCall.args
                    : buildWorkflowCall.args
                        ? JSON.stringify(buildWorkflowCall.args)
                        : undefined)
            : undefined;
        const visibleFunctionCalls = allFunctionCalls.filter(call => call.name !== "build_workflow");
        const visibleToolIconCalls = visibleFunctionCalls
            .filter((call, index, calls) => calls.findIndex(candidate => getToolCallIconType(candidate.name) === getToolCallIconType(call.name)) === index)
            .slice(0, 5);
        const allFileSearchCalls = runDetails.filter(call => call.type === "function" && call.name == "file_search");
        const allCodeInterpreterCalls = runDetails.filter(call => call.type === "code_interpreter");
        const hasInlineTableTag = msg.content?.some((content: any) => /<pqTable\s+id=(['"])\d+\1\s*\/>/i.test(content.text || ''));
        // Keep already-saved conversations readable. New responses always use
        // an inline tag, so they never take this trailing compatibility path.
        const legacyTables = hasInlineTableTag
            ? []
            : runDetails
                .filter(call => call.type === "function" && call.name === "render_ui_table")
                .map(call => parseUITableData(call.args))
                .filter((table): table is UITableData => Boolean(table));
        const funcId = `function-calls-group-${msg.id}`;
        const fileSearchId = `file-search-group-${msg.id}`;
        const codeInterpreterId = `code-interpreter-group-${msg.id}`;
        if (!workflowProposalJson && visibleFunctionCalls.length === 0 && allFileSearchCalls.length === 0 && allCodeInterpreterCalls.length === 0 && legacyTables.length === 0) {
            return <></>;
        }
        return (
            <div key={funcId} className="tool-activity mt-4">
                {legacyTables.map((table, index) => renderUITable(table, `legacy-${msg.id}-${index}`))}
                {/* Workflow Preview for build_workflow tool call */}
                {workflowProposalJson && (
                    <WorkflowPreviewComponent workflowJson={workflowProposalJson} onWorkflowCreated={onWorkflowCreated} />
                )}
                {/* Function calls */}
                {visibleFunctionCalls.length > 0 && (
                    <section className="tool-activity__group">
                        <button
                            type="button"
                            className="tool-activity__summary"
                            onClick={() => handleToggleDetails(funcId)}
                        >
                            <span className="tool-activity__summary-label"><Wrench size={14} />Research actions</span>
                            <span className="tool-activity__avatar-stack" aria-label={`${visibleFunctionCalls.length} research actions`}>
                                {visibleToolIconCalls.map((call, index) => (
                                    <span className="tool-activity__avatar" key={`${call.id}-summary`} title={formatFunctionName(call.name)} style={{ zIndex: 5 - index }}>
                                        {renderToolCallIcon(call.name)}
                                    </span>
                                ))}
                            </span>
                            <span className="tool-activity__summary-meta">{visibleFunctionCalls.length} {visibleFunctionCalls.length === 1 ? 'action' : 'actions'}</span>
                            <ChevronDown className={expandedDetails[funcId] ? 'tool-activity__chevron tool-activity__chevron--open' : 'tool-activity__chevron'} size={15} />
                        </button>
                        {expandedDetails[funcId] && (
                            <div className="tool-activity__list">
                                {visibleFunctionCalls.map((call, idx) => {
                                    if (!call.name || !call.args || !call) return <></>
                                    const callId = call.id;
                                    // Handle args that may already be parsed (e.g., render_ui_table after enrichment)
                                    let argsDisplay: string;
                                    try {
                                        if (typeof call.args === 'string') {
                                            argsDisplay = JSON.stringify(JSON.parse(call.args), null, 2);
                                        } else {
                                            argsDisplay = JSON.stringify(call.args, null, 2);
                                        }
                                    } catch {
                                        argsDisplay = String(call.args);
                                    }
                                    return (
                                        <div key={callId} className="tool-activity__call">
                                            <button
                                                type="button"
                                                className="tool-activity__call-header"
                                                onClick={() => handleToggleDetails(callId)}
                                            >
                                                <span className="tool-activity__call-label">
                                                    <span className="tool-activity__call-icon">{renderToolCallIcon(call.name)}</span>
                                                    <span className="tool-activity__call-name">{formatFunctionName(call.name)}</span>
                                                </span>
                                                <span className={call.output ? 'tool-activity__status' : 'tool-activity__status tool-activity__status--working'}>{call.output ? 'Complete' : 'Working'}</span>
                                                <ChevronDown className={expandedDetails[callId] ? 'tool-activity__chevron tool-activity__chevron--open' : 'tool-activity__chevron'} size={14} />
                                            </button>
                                            {expandedDetails[callId] && (
                                                <div className="tool-activity__detail">
                                                    <div className="tool-activity__detail-label">Arguments</div>
                                                    <pre className="tool-activity__code">{argsDisplay}</pre>
                                                    <div className="tool-activity__detail-label">Result</div>
                                                    <div className="tool-activity__result">
                                                        {call.output ? formatFuncCallOutput(call) : 'Waiting for the result…'}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}

                {/* File search */}
                {allFileSearchCalls.length > 0 && (
                    <section className="tool-activity__group">
                        <button
                            type="button"
                            className="tool-activity__summary"
                            onClick={() => handleToggleDetails(fileSearchId)}
                        >
                            <span className="tool-activity__summary-label"><FileSearch size={14} />Document search</span>
                            <span className="tool-activity__summary-meta">{allFileSearchCalls.length} {allFileSearchCalls.length === 1 ? 'search' : 'searches'}</span>
                            <ChevronDown className={expandedDetails[fileSearchId] ? 'tool-activity__chevron tool-activity__chevron--open' : 'tool-activity__chevron'} size={15} />
                        </button>
                        {expandedDetails[fileSearchId] && (
                            <div className="tool-activity__list">
                                {allFileSearchCalls.map((call, idx) => {
                                    if (!call.output) {
                                        return <div key={call.id || idx} className="tool-activity__empty">Searching documents…</div>;
                                    }
                                    let results = [];
                                    try {
                                        const js = JSON.parse(call.output);
                                        results = js.results;
                                    } catch (e) {
                                        console.error('error parsing call.output', e)
                                        results = [
                                            {
                                                file_name: "error",
                                                content: [
                                                    {
                                                        text: call.output
                                                    }
                                                ]
                                            }
                                        ]
                                    }
                                    return results.map((file, fileIdx) => {
                                        const fileId = `${fileSearchId}-file-${call.id}-${fileIdx}`;
                                        return (
                                            <div key={fileId} className="tool-activity__call">
                                                <button
                                                    type="button"
                                                    className="tool-activity__call-header"
                                                    onClick={() => handleToggleDetails(fileId)}
                                                >
                                                    <span className="tool-activity__call-label">
                                                        <span className="tool-activity__call-icon"><FileText size={14} /></span>
                                                        <span className="tool-activity__call-name">{file.file_name || file.filename || file.file_id}</span>
                                                    </span>
                                                    <ChevronDown className={expandedDetails[fileId] ? 'tool-activity__chevron tool-activity__chevron--open' : 'tool-activity__chevron'} size={14} />
                                                </button>
                                                {expandedDetails[fileId] && (
                                                    <div className="tool-activity__detail tool-activity__source">
                                                        {file.content.map((contentItem, contentIdx) => (
                                                            <p key={contentIdx} style={{ overflowWrap: "break-word" }}>
                                                                {contentItem.text}
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                                )}
                            </div>
                        )}
                    </section>
                )}

                {/* Subtle hint for Code Interpreter Results */}
                {allCodeInterpreterCalls.length > 0 && (
                    <CodeInterpreterResults
                        expandedDetails={expandedDetails}
                        handleToggleDetails={handleToggleDetails}
                        id={codeInterpreterId}
                        allCodeInterpreterCalls={allCodeInterpreterCalls}
                        highlighter={highlighter}
                        openCodeModal={openCodeModal}
                    />
                )}
            </div>
        );
    };

    // Carousel for images from runDetails
    function RenderImageCarousel({ msg }: { msg: Message }) {
        const images = [];
        if (msg.runDetails) {
            for (const call of msg.runDetails) {
                if (call.images && Array.isArray(call.images)) {
                    for (const img of call.images) {
                        images.push(img);
                    }
                }
            }
        }
        const [carouselIdx, setCarouselIdx] = useState(0);
        if (images.length === 0) return null;
        return (
            <div className="w-full flex flex-col items-center my-4">
                <div className="relative w-full max-w-xs flex flex-col items-center">
                    <img
                        src={images[carouselIdx].data}
                        alt={`Image ${carouselIdx + 1}`}
                        className="mb-2 cursor-pointer object-contain w-full max-h-64 rounded shadow"
                        onClick={() => {
                            setImgModalState({
                                isImageModalOpen: true,
                                modalImageSrc: images[carouselIdx].data,
                            });
                        }}
                    />
                    <div className="absolute bottom-2 right-2 flex space-x-4">
                        <button
                            className="bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => {
                                setImgModalState({
                                    isImageModalOpen: true,
                                    modalImageSrc: images[carouselIdx].data,
                                });
                            }}
                        >
                            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                        </button>
                        <button
                            className="bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => {
                                const link = document.createElement("a");
                                link.href = images[carouselIdx].data;
                                link.download = `image-${carouselIdx + 1}.png`;
                                link.click();
                            }}
                        >
                            <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                        <button
                            className="bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => {
                                let hint = undefined;
                                if (msg.content?.length > 0) {
                                    hint = msg.content[0].text
                                }
                                createArtifact("image", images[carouselIdx].data, hint);
                            }}
                        >
                            <BookmarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="flex items-center mt-2 space-x-2">
                    <button
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                        onClick={() => setCarouselIdx((carouselIdx - 1 + images.length) % images.length)}
                        disabled={images.length <= 1}
                    >
                        ◀
                    </button>
                    <span className="text-sm text-gray-600">{carouselIdx + 1} / {images.length}</span>
                    <button
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                        onClick={() => setCarouselIdx((carouselIdx + 1) % images.length)}
                        disabled={images.length <= 1}
                    >
                        ▶
                    </button>
                </div>
            </div>
        );
    };

    // Function to extract the latest title from thinking summary
    function extractLatestTitle(text: string): string {
        if (!text) return "Show Thinking";
        
        // Look for bold text patterns (**text**) which are used as titles in thinking summaries
        const boldTitleRegex = /\*\*([^*]+)\*\*/g;
        const matches = [];
        let match;
        
        while ((match = boldTitleRegex.exec(text)) !== null) {
            matches.push(match[1].trim());
        }
        
        // Return the last found title, or default text if none found
        return matches.length > 0 ? matches[matches.length - 1] : "Show Thinking";
    }

    // Function to convert pipe-separated content to proper markdown tables and handle bullet points
    function convertToMarkdownTable(text: string): string {
        // First, handle bullet points by converting them to proper markdown list format
        const bulletPointRegex = /^[\s]*[•✓][\s]*/gm;
        let processedText = text.replace(bulletPointRegex, '* ');

        // Also handle cases where bullet points appear mid-paragraph
        processedText = processedText.replace(/\n[\s]*[•✓][\s]*/g, '\n* ');

        const lines = processedText.split('\n');
        const result: string[] = [];
        let i = 0;

        // Helper function to format numbers in table cells
        const formatTableCell = (cell: string): string => {
            const trimmed = cell.trim();
            // Check if the cell looks like a number with space separators (e.g., "15 376")
            if (/^\d{1,3}(?:\s\d{3})*$/.test(trimmed)) {
                // Replace spaces with commas for proper number formatting
                return trimmed.replace(/\s/g, ',');
            }
            return trimmed;
        };

        while (i < lines.length) {
            const line = lines[i].trim();

            // Check if this line looks like a table header (contains pipes and isn't empty)
            if (line.includes('|') && line.length > 0) {
                // Look ahead to see if the next few lines also contain pipes
                let tableLines: string[] = [line];
                let j = i + 1;

                // Collect consecutive lines that contain pipes
                while (j < lines.length && lines[j].trim().includes('|') && lines[j].trim().length > 0) {
                    tableLines.push(lines[j].trim());
                    j++;
                }

                // If we found at least 2 lines with pipes, check if it's already a proper markdown table
                if (tableLines.length >= 2) {
                    // Check if the second line is already a separator row (contains only dashes, pipes, and spaces)
                    const secondLine = tableLines[1];
                    const isSeparatorRow = /^[\s\|\-]+$/.test(secondLine);

                    if (isSeparatorRow) {
                        // Already a proper markdown table, just add it as-is but format numbers
                        for (let k = 0; k < tableLines.length; k++) {
                            const tableLine = tableLines[k];
                            if (k === 1) {
                                // Don't format the separator row
                                result.push(tableLine);
                            } else {
                                // Format numbers in data rows
                                const formattedLine = tableLine.replace(/\|([^|]*)\|/g, (match, cell) => {
                                    return `|${formatTableCell(cell)}|`;
                                });
                                result.push(formattedLine);
                            }
                        }
                        result.push(''); // Add empty line after table
                        i = j;
                    } else {
                        // Convert to proper markdown table format
                        const firstLine = tableLines[0];

                        // Check if the first line starts and ends with pipes (already formatted)
                        const isAlreadyFormatted = firstLine.startsWith('|') && firstLine.endsWith('|');

                        if (isAlreadyFormatted) {
                            // Count columns from the first line
                            const columnCount = firstLine.split('|').length - 2; // -2 because of leading and trailing pipes

                            // Add the header row with formatted numbers
                            const formattedHeaderLine = firstLine.replace(/\|([^|]*)\|/g, (match, cell) => {
                                return `|${formatTableCell(cell)}|`;
                            });
                            result.push(formattedHeaderLine);

                            // Add the separator row
                            result.push('|' + ' --- |'.repeat(columnCount));

                            // Add the data rows with formatted numbers
                            for (let k = 1; k < tableLines.length; k++) {
                                const formattedDataLine = tableLines[k].replace(/\|([^|]*)\|/g, (match, cell) => {
                                    return `|${formatTableCell(cell)}|`;
                                });
                                result.push(formattedDataLine);
                            }
                        } else {
                            // Convert pipe-separated format to proper markdown
                            const columnCount = (firstLine.match(/\|/g) || []).length + 1;

                            // Add the header row
                            result.push('| ' + firstLine.split('|').map(cell => formatTableCell(cell)).join(' | ') + ' |');

                            // Add the separator row
                            result.push('|' + ' --- |'.repeat(columnCount));

                            // Add the data rows
                            for (let k = 1; k < tableLines.length; k++) {
                                result.push('| ' + tableLines[k].split('|').map(cell => formatTableCell(cell)).join(' | ') + ' |');
                            }
                        }

                        result.push(''); // Add empty line after table
                        i = j; // Skip the processed lines
                    }
                } else {
                    result.push(line);
                    i++;
                }
            } else {
                result.push(line);
                i++;
            }
        }

        return result.join('\n');
    }

    return (
        <>
            <div className="conversation-surface flex flex-col h-full">
                {/* Sticky Header */}
                <div className="flex items-center sticky top-0 z-10 p-4 gap-2">
                    <img
                        src={`/company_icons/${convo?.ticker}.png`}
                        alt={`${convo?.ticker}-logo`}
                        className="w-8 h-8 flex-shrink-0"
                        onError={(e) => {
                            console.log('img err', e)
                            e.currentTarget.style.display = 'none'
                        }}
                    />
                    <h1 className="font-bold flex items-center flex-1 min-w-0">
                        <span className="truncate">{convo?.name}</span>
                    </h1>

                    {user?.user?.plan === "free" && (
                        <a
                            href="#"
                            onClick={async () => {
                                if (process.env.REACT_APP_PQ_PREMIUM_PRICE_ID === undefined) {
                                    dispatch(setNotification({ message: 'Checkout is unavailable right now.', type: 'error' }));
                                    return;
                                }
                                const res = await dispatch(
                                    createCheckoutSession({
                                        productCode: process.env.REACT_APP_PQ_PRO_PRICE_ID,
                                    })
                                );
                                if (res.meta.requestStatus === "fulfilled") {
                                    window.location.href = (res.payload as any).redirectUrl;
                                } else {
                                    dispatch(setNotification({ message: 'Checkout is unavailable right now.', type: 'error' }));
                                }
                            }}
                            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 text-orange-600 font-semibold border-2 border-orange-600 rounded-lg hover:bg-orange-50 transition flex-shrink-0"
                        >
                            <span className="text-purple-600 text-sm">💎</span>
                            <span className="hidden xs:inline">Upgrade</span>
                            <span className="xs:hidden">Up</span>
                        </a>
                    )}

                    {/* <select
                        className='w-1/4 ml-auto bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 appearance-none'
                        value={selectedModel} 
                        onChange={(e)=>{
                            setSelectedModel(e.target.value)
                        }}
                     
                    >
                        <option value="gpt-5.4">GPT 5.4</option>
                        <option value="GPT-5">GPT 5</option>
                        <option value="o3-pro">o3 Pro</option>
                        <option value="o3">o3</option>
                        <option value="o3-mini">o3-mini</option>
                        <option value="o4-mini">o4-mini</option>
                        <option value="gpt-4o">GPT 4o</option>
                        <option value="gpt-4o-mini">GPT 4o-mini</option>
                        <option value="gpt-4.1">GPT 4.1</option>
                        <option value="gpt-4.1-mini">GPT 4.1-mini</option>
                        <option value="gpt-4.1-nano">GPT 4.1-nano</option>
                    </select> */}
                </div>


                {/* Scrollable Messages Section */}
                <div
                    id="message-div"
                    className="flex-1 flex flex-col space-y-6 mt-3 scroll-smooth overflow-auto px-4 py-6"
                    style={{ maxWidth: '100%', minWidth: 0 }}
                >
                    {/* Empty conversation - show suggested topics */}
                    {(!convo?.messages || convo.messages.length === 0) && questionSuggestions.length > 0 && (
                        <div className="flex-1 flex flex-col items-center justify-center w-full">
                            <h2 className="text-xl font-semibold text-gray-700 mb-6">Start your research</h2>
                            <div className="grid grid-cols-1 gap-4 max-w-2xl w-full">
                                {questionSuggestions.slice(0, 3).map((suggestion, idx) => (
                                    <button
                                        key={`suggestion-${idx}`}
                                        onClick={() => {
                                            setMessage(suggestion);
                                            sendMessageLocal(suggestion);
                                        }}
                                        className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
                                    >
                                        <div className="flex items-center justify-between">
                                            <p className="text-gray-700 group-hover:text-gray-900 font-medium">
                                                {suggestion}
                                            </p>
                                            <svg
                                                className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {convo &&
                        convo.messages &&
                        convo.messages.map((msg: any, msgIdx: number) => {
                            if (msg.role === Message.RoleEnum.User) {
                                return (
                                    <div key={`msg-${msg.id}`} className="flex justify-end">
                                        <div className="bg-blue-500 rounded-2xl rounded-br-md py-3 px-4 max-w-sm text-white shadow-sm">
                                            {msg.content.map((c: any, i: any) => (
                                                <p key={`content-${msg.id}-${i}`} className="text-sm leading-relaxed">{c.text}</p>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div key={`msg-${msg.id}`} className="flex justify-center px-4">
                                    <div
                                        className="py-4 px-5 max-w-5xl w-full"
                                        style={{
                                            overflowWrap: 'break-word',
                                            wordBreak: 'break-word',
                                            hyphens: 'auto',
                                            minWidth: 0, // Allows flex items to shrink below content size
                                        }}
                                    >
                                    <RenderImageCarousel msg={msg} />
                                    {msg?.content?.map((c: any, i: any) =>
                                        addCitationLinksToMessage(c, msg, i)
                                    )}

                                    {/* Reasoning Summary Toggle Section */}
                                    {msg.thinkingSummary && (
                                        <div className="mt-2">
                                            <button
                                                className="flex items-center text-gray-600 hover:text-gray-900 focus:outline-none"
                                                onClick={() => handleToggleDetails(`reasoning-summary-${msg.id}`)}
                                            >
                                                <Brain size={15} className="mr-2 text-[#53716b]" aria-hidden="true" />
                                                <span className={`font-medium ${messageInProgress && msgIdx === convo.messages.length - 1 ? 'animate-pulse' : ''}`}>{extractLatestTitle(msg.thinkingSummary)}</span>
                                            </button>
                                            {expandedDetails[`reasoning-summary-${msg.id}`] && (
                                                <div className="ml-5 mt-1 p-2 border-l-4 border-gray-300 bg-gray-50 rounded text-sm text-gray-800">
                                                    <ReactMarkdown
                                                        key={`thinking-summary-markdown-${msg.id}`}
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            p: ({ node, ...props }) => (
                                                                <p key={`p-${msg.id}`} className="mb-2" {...props} />
                                                            ),
                                                            ul: ({ node, ...props }) => (
                                                                <ul key={`ul-${msg.id}`} className="list-disc ml-5" {...props} />
                                                            ),
                                                            li: ({ node, ...props }) => (
                                                                <li key={`li-${msg.id}`} className="mb-1" {...props} />
                                                            ),
                                                            a: ({ node, ...props }) => (
                                                                <a key={`a-${msg.id}`} className="text-blue-500 underline" {...props} />
                                                            ),
                                                            table: ({ node, ...props }) => {
                                                                const tableId = `table-thinking-${msg.id}-${Math.random().toString(36).substr(2, 9)}`;
                                                                return (
                                                                    <div className="overflow-x-auto my-4 relative">
                                                                        <div className="flex justify-end mb-2 gap-2">
                                                                            <button
                                                                                onClick={() => {
                                                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                                                    if (tableElement) {
                                                                                        const { data, datasets } = extractChartDataFromTable(tableElement);
                                                                                        setChartData({ data, datasets });
                                                                                        setChartModalOpen(true);
                                                                                    }
                                                                                }}
                                                                                className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                                                title="Create Chart"
                                                                            >
                                                                                <ChartBarIcon className="w-3 h-3" />
                                                                                Chart
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                                                    if (tableElement) {
                                                                                        exportTableToCSV(tableElement, 'financial-data');
                                                                                    }
                                                                                }}
                                                                                className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                                                title="Export to CSV"
                                                                            >
                                                                                <ArrowDownTrayIcon className="w-3 h-3" />
                                                                                CSV
                                                                            </button>
                                                                        </div>
                                                                        <table
                                                                            key={`table-${msg.id}`}
                                                                            className="min-w-full border-collapse border border-gray-300"
                                                                            data-table-id={tableId}
                                                                            {...props}
                                                                        />
                                                                    </div>
                                                                );
                                                            },
                                                            thead: ({ node, ...props }) => (
                                                                <thead key={`thead-${msg.id}`} className="bg-gray-50" {...props} />
                                                            ),
                                                            tbody: ({ node, ...props }) => (
                                                                <tbody key={`tbody-${msg.id}`} {...props} />
                                                            ),
                                                            tr: ({ node, ...props }) => (
                                                                <tr key={`tr-${msg.id}`} className="border-b border-gray-200" {...props} />
                                                            ),
                                                            th: ({ node, ...props }) => (
                                                                <th key={`th-${msg.id}`} className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                                                            ),
                                                            td: ({ node, ...props }) => (
                                                                <td key={`td-${msg.id}`} className="border border-gray-300 px-4 py-2" {...props} />
                                                            )
                                                        }}
                                                    >
                                                        {msg.thinkingSummary}
                                                    </ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {msg.runDetails && renderRunDetails(msg)}
                                    </div>
                                </div>
                            );
                        })}

                    {convo && convo?.messages[convo.messages?.length - 1]?.role === 'user' && (
                        <div className="flex justify-center px-4">
                            <div className="py-4 px-5 max-w-5xl w-full">
                                <TypingDots />
                            </div>
                        </div>
                    )}
                </div>

                {/* Sticky Footer */}
                <div className="sticky bottom-0 z-10 pl-4 pr-4 pt-4">
                    <div className="relative">
                        <InputWithSuggestions suggestions={questionSuggestions} value={message} sendMessage={sendMessageLocal} setMessage={setMessage} disabled={messageInProgress || messageInFlight} />
                    </div>

                    {/* <div
                        className={
                            `mr-auto mt-2 flex items-center p-2 bg-gray-200 w-fit rounded-lg gap-1 ` +
                            (selectedModel === 'o3-pro' ? ' text-blue-800' : ' text-gray-700')
                        }
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            if (selectedModel !== 'o3-pro') {
                                setSelectedModel('o3-pro');
                                setEffortLevel('high');
                            } else {
                                setSelectedModel('o3');
                                setEffortLevel('low');
                            }
                        }}
                    >
                        Deep Research
                        <Telescope className="h-5 w-5 ml-1" />
                    </div> */}
                    {/* Warning Message */}
                    <div className="flex items-center mt-2">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="orange" />
                        </svg>
                        <p
                            className="text-sm text-gray-500 flex items-center"
                            style={{
                                maxHeight: '4vh',
                                overflow: 'auto',
                            }}
                        >
                            AI makes mistakes. Check the facts.
                        </p>
                    </div>
                </div>
            </div >
            {
                tooltip.visible && (
                    <div
                        className="absolute bg-white border border-gray-300 rounded-lg p-2 shadow-lg overflow-y-auto"
                        style={{
                            top: tooltip.position.y,
                            left: tooltip.position.x,
                            maxHeight: '200px', // Adjust the height as needed
                            width: '200px', // Adjust the width as needed
                            zIndex: 1000
                        }}
                        onMouseLeave={hideTooltip}
                    >
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: tooltip.title }}></div>
                    </div>
                )
            }
            <FinancialChartModal
                isOpen={chartModalOpen}
                onClose={() => setChartModalOpen(false)}
                data={chartData.data}
                datasets={chartData.datasets}
            />
        </>
    )
};
